// i18nOwl-ignore [userPropertyDataType.boolean, userPropertyDataType.date, userPropertyDataType.number, userPropertyDataType.string]
import type {BrevoContactAttribute} from '@cohort/merchants/apps/brevo/sync/utils';
import {
  BREVO_USER_PROPERTY_PREFIX,
  buildDefaultUserPropertyColumn,
  getDataTypeFromBrevoAttributeType,
} from '@cohort/merchants/apps/brevo/sync/utils';
import {Badge} from '@cohort/merchants/components/Badge';
import Button from '@cohort/merchants/components/buttons/Button';
import DataTypeIcon from '@cohort/merchants/components/DataTypeIcon';
import {SelectPicker} from '@cohort/merchants/components/form/select/SelectPicker';
import {useCohortForm} from '@cohort/merchants/hooks/contexts/form';
import type {SyncConfigFormValues} from '@cohort/merchants/pages/apps/app/utils';
import {ArrowRight, Trash} from '@phosphor-icons/react';
import {Fragment} from 'react';
import {useFieldArray} from 'react-hook-form';
import {useTranslation} from 'react-i18next';

interface ImportUsersFieldSelectProps {
  index: number;
  brevoContactAttributes: BrevoContactAttribute[];
  selectedAttribute: BrevoContactAttribute | null;
}

const ImportUsersFieldSelect: React.FC<ImportUsersFieldSelectProps> = ({
  index,
  brevoContactAttributes,
  selectedAttribute,
}) => {
  const {t} = useTranslation('app-brevo', {
    keyPrefix: 'sync.importUsersFieldSelect',
  });

  const {control, watch} = useCohortForm<Extract<SyncConfigFormValues, {appId: 'brevo'}>>();

  const isImportEnabled = watch('userImportEnabled');
  const userSyncConfig = watch('userSyncConfig');

  const {remove: removeUserPropertyColumn, replace: replaceUserPropertyColumns} = useFieldArray({
    control,
    name: 'userSyncConfig.userPropertyColumns',
  });
  const selectedAttributeNames = userSyncConfig?.userPropertyColumns.map(
    column => column.columnName
  );

  const handleDeleteField = (index: number): void => {
    if (userSyncConfig?.userPropertyColumns.length === 1) {
      // When deleting the last field, we reinitialize it to always have at least one field
      replaceUserPropertyColumns([buildDefaultUserPropertyColumn()]);
      return;
    }

    removeUserPropertyColumn(index);
  };

  const handleAttributeChange = (index: number, attribute: BrevoContactAttribute | null): void => {
    const updatedUserPropertyColumns = [...(userSyncConfig?.userPropertyColumns ?? [])];

    if (updatedUserPropertyColumns[index] !== undefined && attribute !== null) {
      updatedUserPropertyColumns[index] = {
        columnName: attribute.name,
        referenceId: attribute.name,
        propertyName: `${BREVO_USER_PROPERTY_PREFIX} ${attribute.name}`,
        dataType: getDataTypeFromBrevoAttributeType(attribute.type),
      };
      replaceUserPropertyColumns(updatedUserPropertyColumns);
    }
  };

  const attributeOptions = brevoContactAttributes.map(attr => ({
    label: attr.name,
    value: attr.name,
    isDisabled: selectedAttributeNames?.includes(attr.name),
  }));

  const value =
    selectedAttribute !== null
      ? {value: selectedAttribute.name, label: selectedAttribute.name}
      : null;
  const dataType =
    selectedAttribute !== null
      ? getDataTypeFromBrevoAttributeType(selectedAttribute.type)
      : 'string';
  const isLoneEmptyField =
    userSyncConfig?.userPropertyColumns.length === 1 &&
    userSyncConfig.userPropertyColumns[0]?.referenceId === '';

  return (
    <div key={index} className="flex items-center gap-6">
      <div className="w-[276px]">
        <SelectPicker
          name={`attribute-${index}`}
          placeholder={t('placeholder')}
          options={attributeOptions}
          value={value}
          isDisabled={!isImportEnabled}
          onChange={option => {
            handleAttributeChange(
              index,
              brevoContactAttributes.find(attr => attr.name === option?.value) ?? null
            );
          }}
        />
      </div>
      <ArrowRight size={20} className="text-slate-400" />
      <div className="flex flex-1 items-center justify-between">
        {selectedAttribute !== null ? (
          <Fragment>
            <div>
              <Badge
                text={`${BREVO_USER_PROPERTY_PREFIX} ${selectedAttribute.name}`}
                textColor="text-slate-700"
                backgroundColor="bg-slate-100"
              />
            </div>
            <div className="flex items-center gap-x-1.5">
              <DataTypeIcon dataType={dataType} />
              <div className="text-sm font-normal text-slate-700">
                {t(`userPropertyDataType.${dataType}`)}
              </div>
            </div>
          </Fragment>
        ) : (
          <p className="text-slate-400">{t('noAttributeSelected')}</p>
        )}
      </div>
      <Button
        variant="secondary"
        size="icon"
        onClick={() => handleDeleteField(index)}
        disabled={isLoneEmptyField}
      >
        <Trash size={20} className="text-red-400" />
      </Button>
    </div>
  );
};

export default ImportUsersFieldSelect;
